import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { EmptyState } from '@/components/shared/EmptyState';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import {
  Users,
  Plus,
  Search,
  MoreVertical,
  Shield,
  UserCheck,
  UserX,
  Mail,
  Filter,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  superadmin: { label: 'Super Admin', color: 'bg-purple-100 text-purple-800' },
  platform_admin: { label: 'Platform Admin', color: 'bg-blue-100 text-blue-800' },
  org_admin: { label: 'Org Admin', color: 'bg-sky-100 text-sky-800' },
  org_user: { label: 'User', color: 'bg-slate-100 text-slate-800' },
  viewer: { label: 'Viewer', color: 'bg-gray-100 text-gray-800' },
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-800',
  inactive: 'bg-red-100 text-red-800',
  invited: 'bg-amber-100 text-amber-800',
  pending: 'bg-yellow-100 text-yellow-800',
};

export default function UserManagementPage() {
  const { user: currentUser, hasRole, hasPermission } = useAuth();
  const [users, setUsers] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterOrg, setFilterOrg] = useState('all');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteData, setInviteData] = useState({ email: '', name: '', role: 'org_user', org_id: '' });
  const [pendingUsers, setPendingUsers] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterRole !== 'all') params.append('role', filterRole);
      if (filterStatus !== 'all') params.append('status', filterStatus);
      if (filterOrg !== 'all') params.append('org_id', filterOrg);

      const [usersRes, orgsRes] = await Promise.all([
        axios.get(`${API}/users?${params.toString()}`),
        hasRole(['superadmin', 'platform_admin']) ? axios.get(`${API}/organizations`) : Promise.resolve({ data: [] }),
      ]);
      setUsers(usersRes.data);
      setOrganizations(orgsRes.data);
      // Fetch pending users for approval section
      if (hasPermission('user.manage')) {
        try {
          const pendingRes = await axios.get(`${API}/users/pending/list`);
          setPendingUsers(pendingRes.data);
        } catch { /* no pending endpoint before init */ }
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  }, [filterRole, filterStatus, filterOrg, hasRole, hasPermission]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInviteUser = async () => {
    try {
      await axios.post(`${API}/users/invite`, inviteData);
      setInviteDialogOpen(false);
      setInviteData({ email: '', name: '', role: 'org_user', org_id: '' });
      fetchData();
    } catch (error) {
      console.error('Failed to invite user:', error);
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/activate`);
      fetchData();
    } catch (error) {
      console.error('Failed to activate user:', error);
    }
  };

  const handleDeactivateUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/deactivate`);
      fetchData();
    } catch (error) {
      console.error('Failed to deactivate user:', error);
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.patch(`${API}/users/${userId}`, { role: newRole });
      fetchData();
    } catch (error) {
      console.error('Failed to change role:', error);
      toast.error(error.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleApproveUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/approve`);
      toast.success('User approved');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleRejectUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/reject`);
      toast.success('User rejected');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  const filteredUsers = users.filter((u) =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!hasPermission('user.read')) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-500">You don't have permission to manage users.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="user-management-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">User Management</h1>
            <p className="text-slate-500">Manage users and their roles</p>
          </div>
          {hasPermission('user.invite') && (
            <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="invite-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Invite User
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Invite New User</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label htmlFor="invite-email">Email</Label>
                    <Input
                      id="invite-email"
                      type="email"
                      placeholder="user@company.com"
                      value={inviteData.email}
                      onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })}
                      data-testid="invite-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="invite-name">Name</Label>
                    <Input
                      id="invite-name"
                      placeholder="Full Name"
                      value={inviteData.name}
                      onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })}
                      data-testid="invite-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Role</Label>
                    <Select
                      value={inviteData.role}
                      onValueChange={(value) => setInviteData({ ...inviteData, role: value })}
                    >
                      <SelectTrigger data-testid="invite-role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {currentUser?.role === 'superadmin' && (
                          <>
                            <SelectItem value="platform_admin">Platform Admin</SelectItem>
                            <SelectItem value="org_admin">Org Admin</SelectItem>
                          </>
                        )}
                        {['superadmin', 'platform_admin', 'org_admin'].includes(currentUser?.role) && (
                          <SelectItem value="org_admin">Org Admin</SelectItem>
                        )}
                        <SelectItem value="org_user">User</SelectItem>
                        <SelectItem value="viewer">Viewer</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {hasRole(['superadmin', 'platform_admin']) && organizations.length > 0 && (
                    <div className="space-y-2">
                      <Label>Organization</Label>
                      <Select
                        value={inviteData.org_id}
                        onValueChange={(value) => setInviteData({ ...inviteData, org_id: value })}
                      >
                        <SelectTrigger data-testid="invite-org-select">
                          <SelectValue placeholder="Select organization" />
                        </SelectTrigger>
                        <SelectContent>
                          {organizations.map((org) => (
                            <SelectItem key={org.id} value={org.id}>
                              {org.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <Button
                    onClick={handleInviteUser}
                    className="w-full"
                    disabled={!inviteData.email}
                    data-testid="invite-submit-btn"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send Invitation
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search users..."
                    className="pl-10"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="user-search-input"
                  />
                </div>
              </div>
              <Select value={filterRole} onValueChange={setFilterRole}>
                <SelectTrigger className="w-[150px]" data-testid="filter-role">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="superadmin">Super Admin</SelectItem>
                  <SelectItem value="platform_admin">Platform Admin</SelectItem>
                  <SelectItem value="org_admin">Org Admin</SelectItem>
                  <SelectItem value="org_user">User</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[150px]" data-testid="filter-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="invited">Invited</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                </SelectContent>
              </Select>
              {hasRole(['superadmin', 'platform_admin']) && organizations.length > 0 && (
                <Select value={filterOrg} onValueChange={setFilterOrg}>
                  <SelectTrigger className="w-[180px]" data-testid="filter-org">
                    <SelectValue placeholder="Organization" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pending Approvals */}
        {pendingUsers.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-800">
                <UserCheck className="w-5 h-5" />
                Pending Approvals ({pendingUsers.length})
              </CardTitle>
              <CardDescription>Users awaiting admin approval to access the platform</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {pendingUsers.map((u) => (
                  <div key={u.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-200" data-testid={`pending-${u.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-medium">
                        {u.name?.charAt(0) || u.email.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{u.name || u.email}</p>
                        <p className="text-sm text-slate-500">{u.email} • Registered {new Date(u.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleApproveUser(u.id)} data-testid={`approve-${u.id}`}>
                        <UserCheck className="w-4 h-4 mr-1" /> Approve
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleRejectUser(u.id)} data-testid={`reject-${u.id}`}>
                        <UserX className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Table */}
        <Card>
          <CardHeader>
            <CardTitle>Users</CardTitle>
            <CardDescription>{filteredUsers.length} users found</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">User</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Role</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Organization</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Last Login</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => (
                      <tr
                        key={user.id}
                        className="border-b border-slate-100 hover:bg-slate-50"
                        data-testid={`user-row-${user.id}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-sky-100 flex items-center justify-center text-sky-600 font-medium">
                              {user.name?.charAt(0) || user.email.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <p className="font-medium text-slate-900">{user.name || 'No name'}</p>
                              <p className="text-sm text-slate-500">{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={ROLE_LABELS[user.role]?.color || 'bg-slate-100'}>
                            {ROLE_LABELS[user.role]?.label || user.role}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge className={STATUS_COLORS[user.status] || 'bg-slate-100'}>
                            {user.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-600">
                          {organizations.find((o) => o.id === user.org_id)?.name || '-'}
                        </td>
                        <td className="py-3 px-4 text-sm text-slate-500">
                          {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {hasPermission('user.manage') && user.id !== currentUser?.id && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`user-actions-${user.id}`}>
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {user.status === 'active' ? (
                                  <DropdownMenuItem onClick={() => handleDeactivateUser(user.id)}>
                                    <UserX className="w-4 h-4 mr-2" />
                                    Deactivate
                                  </DropdownMenuItem>
                                ) : (
                                  <DropdownMenuItem onClick={() => handleActivateUser(user.id)}>
                                    <UserCheck className="w-4 h-4 mr-2" />
                                    Activate
                                  </DropdownMenuItem>
                                )}
                                {hasRole(['superadmin', 'platform_admin', 'org_admin']) && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleChangeRole(user.id, 'org_admin')}>
                                      <Shield className="w-4 h-4 mr-2" />
                                      Make Org Admin
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleChangeRole(user.id, 'org_user')}>
                                      <Users className="w-4 h-4 mr-2" />
                                      Make User
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredUsers.length === 0 && (
                  <div className="py-8 border-t border-slate-200">
                    <EmptyState
                      icon={Users}
                      title="No users found"
                      description={searchQuery ? "Try adjusting your search or filters." : "Invite team members to start collaborating."}
                      actionLabel={!searchQuery ? "Invite user" : undefined}
                      onAction={!searchQuery ? () => setShowInviteDialog(true) : undefined}
                    />
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
